import React, {createContext, useContext, useState} from 'react'
import styled from 'styled-components'
import Button from '@material-ui/core/Button'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'
import SuccessAlert from 'lib/ui/alerts/SuccessAlert'
import Menu from 'lib/ui/Menu'
import ExportAttendeeZoomLogSummaryMenuItem from './ExportAttendeeZoomLogSummaryMenuItem'
import ExportAttendeeZoomLogDetailedMenuItem from './ExportAttendeeZoomLogDetailedMenuItem'
import ExportEventRoomAssignmentsMenuItem from './ExportEventRoomAssignmentsMenuItem'
import {useAreas} from 'organization/Event/AreasProvider'

type ExportEventLogsContextValue = {
  error: string | null
  success: string | null
  setError: (error: string | null) => void
  setSuccess: (success: string | null) => void
}

const ExportEventLogsContext = createContext<ExportEventLogsContextValue | null>(
  null,
)

function useExportEventLogsContext() {
  const context = useContext(ExportEventLogsContext)
  if (!context) {
    throw new Error(
      'Export event logs components must be used within ExportEventLogsProvider',
    )
  }

  return context
}

export function ExportEventLogsProvider(props: {children: React.ReactNode}) {
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  return (
    <ExportEventLogsContext.Provider
      value={{error, success, setError, setSuccess}}
    >
      {props.children}
    </ExportEventLogsContext.Provider>
  )
}

export function ExportEventLogsMenu() {
  const {setError, setSuccess} = useExportEventLogsContext()

  const {areas} = useAreas()

  if (!areas.length) {
    return null
  }

  return (
    <Menu
      button={({open}) => (
        <Button
          color="primary"
          variant="outlined"
          aria-label="export event log"
          onClick={open}
        >
          Export Log
        </Button>
      )}
    >
      <ExportAttendeeZoomLogSummaryMenuItem
        setError={setError}
        setSuccess={setSuccess}
      />
      <ExportAttendeeZoomLogDetailedMenuItem
        setError={setError}
        setSuccess={setSuccess}
      />
      <ExportEventRoomAssignmentsMenuItem
        setError={setError}
        setSuccess={setSuccess}
      />
    </Menu>
  )
}

export function ExportEventLogsAlerts() {
  const {error, success} = useExportEventLogsContext()

  return (
    <ExportAlerts>
      <ErrorAlert>{error}</ErrorAlert>
      <SuccessAlert>{success}</SuccessAlert>
    </ExportAlerts>
  )
}

const ExportAlerts = styled.div`
  width: 100%;

  .MuiAlert-root {
    width: 100%;
  }
`
